import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

/**
 * /invoices moved to the Accounts page's Payables tab. A link that carried
 * the bill (`/invoices?id=12`) used to lose it in a literal redirect and land
 * on the list; the id now travels as `bill=`, which the Payables tab opens.
 */
function InvoicesRedirect() {
  const { search } = useLocation();
  const id = new URLSearchParams(search).get('id');
  return <Navigate to={id ? `/accounts?tab=invoices&bill=${encodeURIComponent(id)}` : '/accounts?tab=invoices'} replace />;
}
import { AuthProvider } from './contexts/AuthContext';
import { MetricRegistryProvider } from './components/MetricInfoTooltip';
import { TenantAuthProvider } from './contexts/TenantAuthContext';
import { ContractorAuthProvider } from './contexts/ContractorAuthContext';
import { MarketplaceAuthProvider } from './contexts/MarketplaceAuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import './App.css';

// Sprint 1B — eager-load only the entry surfaces. Everything heavier is
// pulled in lazily so the initial JS payload drops sharply on first load.
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

// Auth + marketing (light)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const TermsPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));
const BetaAgreementPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.BetaAgreementPage })));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));

// Admin surface (heavy)
const UsersPage = lazy(() => import('./pages/UsersPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));  // HELP-1
const CompanyAccessPage = lazy(() => import('./pages/admin/CompanyAccessPage'));
const FeedbackInboxPage = lazy(() => import('./pages/admin/FeedbackInboxPage'));
const CustomerHealthPage = lazy(() => import('./pages/admin/CustomerHealthPage'));
const DataIntegrityPage = lazy(() => import('./pages/admin/DataIntegrityPage'));
// NI-8 — AI Operations: usage, cost, failures, corrections, the trust log.
const AIOperationsPage = lazy(() => import('./pages/intelligence/OperationsPage'));
const AskNorthlinePage = lazy(() => import('./pages/intelligence/AskPage'));
const AccountsPage = lazy(() => import('./pages/AccountsPage'));
const PlaidOAuthReturnPage = lazy(() => import('./pages/PlaidOAuthReturnPage'));
const ChartOfAccountsPage = lazy(() => import('./pages/ChartOfAccountsPage'));
const ReconcilePage = lazy(() => import('./pages/ReconcilePage'));
const DealsListPage = lazy(() => import('./pages/deals/DealsListPage'));
const OffersListPage = lazy(() => import('./pages/deals/OffersListPage'));
const DealDetailPage = lazy(() => import('./pages/deals/DealDetailPage'));
const TwoFactorSetupPage = lazy(() => import('./pages/TwoFactorSetupPage'));
const UnderwritingModelRedirect = lazy(() => import('./pages/deals/UnderwritingModelRedirect'));
const PropertiesListPage = lazy(() => import('./pages/properties/PropertiesListPage'));
const PropertyDetailPage = lazy(() => import('./pages/properties/PropertyDetailPage'));
const TenantsPage = lazy(() => import('./pages/TenantsPage'));
const TenantDetailPage = lazy(() => import('./pages/tenants/TenantDetailPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const WorkflowPage = lazy(() => import('./pages/WorkflowPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const BulkImportPage = lazy(() => import('./pages/BulkImportPage'));
const TrashPage = lazy(() => import('./pages/TrashPage'));
const ContractorSection = lazy(() => import('./pages/ContractorSection'));
// ALLOC-9 — the other two People rosters.
const VendorsPage = lazy(() => import('./pages/people/VendorsPage'));
const CustomersPage = lazy(() => import('./pages/people/CustomersPage'));
const ConstructionPage = lazy(() => import('./pages/construction/ConstructionPage'));
// PORT-6. The project's own detail route. Construction used to link a
// project back to its property's Projects tab to do the actual work.
const ProjectDetailPage = lazy(() => import('./pages/construction/ProjectDetailPage'));
// PORT-13. The draw's own route. A draw could only be named through its
// property AND its project, so every alert holding a draw id rebuilt a
// property path — and PORT-8 deleted the screen those paths named.
const DrawDetailPage = lazy(() => import('./pages/construction/DrawDetailPage'));
// PORT-13. Retired property tabs, redirected rather than 404'd — see below.
const RetiredPropertyTab = lazy(() => import('./pages/properties/RetiredPropertyTab'));
// PORT-9 — Finance owns budgeting and capital events. Both were tabs
// inside a property record, so portfolio-wide questions ("which
// properties are behind plan?", "what did we refinance this year?")
// could only be answered one property at a time.
const BudgetsPage = lazy(() => import('./pages/finance/BudgetsPage'));
const CapitalEventsPage = lazy(() => import('./pages/finance/CapitalEventsPage'));
// PORT-2. InspectionsPage has existed since Iter 244 — a complete hub with
// create, checklist, findings, photos and follow-up work orders — and was
// routed nowhere. The only way to reach any of it was the property tab that
// imports its pieces, so inspections were reachable one property at a time.
const InspectionsPage = lazy(() => import('./pages/InspectionsPage'));
// PORT-2. The portfolio-wide maintenance desk. The endpoint behind it
// (GET /work-orders) already existed for the scheduling grid; there was
// simply no screen that answered "what needs doing across the portfolio".
const WorkOrdersPage = lazy(() => import('./pages/WorkOrdersPage'));
// PORT-5. The work order's own detail route. It used to have none — the
// desk sent a row click to /properties/:id?tab=maintenance, so the module
// was a list of links back into Portfolio.
const WorkOrderDetailPage = lazy(() => import('./pages/workorders/WorkOrderDetailPage'));
const JobRequestPage = lazy(() => import('./pages/workorders/JobRequestPage'));  // APR-6

// Tenant Portal (separate bundle)
const TenantLoginPage = lazy(() => import('./pages/tenant/TenantLoginPage'));
const PortalAcceptInvitePage = lazy(() => import('./pages/PortalAcceptInvitePage'));
const TenantDashboard = lazy(() => import('./pages/tenant/TenantDashboard'));
const TenantPayments = lazy(() => import('./pages/tenant/TenantPayments'));
const TenantMaintenance = lazy(() => import('./pages/tenant/TenantMaintenance'));
const TenantMessages = lazy(() => import('./pages/tenant/TenantMessages'));

// Contractor Portal (separate bundle)
const ContractorLoginPage = lazy(() => import('./pages/contractor/ContractorLoginPage'));
// PM-1 — Property Manager portal, and the owner's People → Property Managers.
const PmLoginPage = lazy(() => import('./pages/pm/PmLoginPage'));
const PmHomePage = lazy(() => import('./pages/pm/PmHomePage'));
const PmLayout = lazy(() => import('./pages/pm/PmLayout'));
const PmPropertiesPage = lazy(() => import('./pages/pm/PmPropertiesPage'));
const PmWorkOrdersPage = lazy(() => import('./pages/pm/PmWorkOrdersPage'));
const PmTenantsPage = lazy(() => import('./pages/pm/PmTenantsPage'));
const PmInspectionsPage = lazy(() => import('./pages/pm/PmInspectionsPage'));
const PmDocumentsPage = lazy(() => import('./pages/pm/PmDocumentsPage'));
const PmTasksPage = lazy(() => import('./pages/pm/PmTasksPage'));
const PmInvoicesPage = lazy(() => import('./pages/pm/PmInvoicesPage'));
const PmNotificationsPage = lazy(() => import('./pages/pm/PmNotificationsPage'));
const PmReportsPage = lazy(() => import('./pages/pm/PmReportsPage'));
const PropertyManagersPage = lazy(() => import('./pages/people/PropertyManagersPage'));
const ContractorDashboard = lazy(() => import('./pages/contractor/ContractorDashboard'));
const ContractorProjectDetail = lazy(() => import('./pages/contractor/ContractorProjectDetail'));
const ContractorWorkOrderWizard = lazy(() => import('./pages/contractor/ContractorWorkOrderWizard'));
const ContractorInbox = lazy(() => import('./pages/contractor/ContractorInbox'));
const ContractorWorkspace = lazy(() => import('./pages/contractor/ContractorWorkspace'));
const ContractorProfile = lazy(() => import('./pages/contractor/ContractorProfile'));
const ContractorGalleryPage = lazy(() => import('./pages/contractor/ContractorGalleryPage'));

// Marketplace (hidden from sidebar — Sprint 1B — still reachable via direct URL)
const MarketplaceListingsPage = lazy(() => import('./pages/marketplace/MarketplaceListingsPage'));
const MarketplaceListingDetailPage = lazy(() => import('./pages/marketplace/MarketplaceListingDetailPage'));
const MarketplaceLoginPage = lazy(() => import('./pages/marketplace/MarketplaceLoginPage'));
const MarketplaceDashboardPage = lazy(() => import('./pages/marketplace/MarketplaceDashboardPage'));
const MarketplaceListingFormPage = lazy(() => import('./pages/marketplace/MarketplaceListingFormPage'));

// Public signing / application
const PublicDocumentSign = lazy(() => import('./pages/public/PublicDocumentSign'));
const PublicApplication = lazy(() => import('./pages/public/PublicApplication'));
const StatusPage = lazy(() => import('./pages/public/StatusPage'));  // STATUS-1
const LeaseRedirect = lazy(() => import('./pages/public/LeaseRedirect'));

// Stripe checkout return shells
const ContractorCheckoutComplete = lazy(() =>
  import('./pages/payments/ContractorCheckoutComplete').then(m => ({ default: m.default }))
);
const ContractorCheckoutCancel = lazy(() =>
  import('./pages/payments/ContractorCheckoutComplete').then(m => ({ default: m.ContractorCheckoutCancel }))
);
const DrawCheckoutComplete = lazy(() =>
  import('./pages/payments/DrawCheckoutComplete').then(m => ({ default: m.default }))
);
const DrawCheckoutCancel = lazy(() =>
  import('./pages/payments/DrawCheckoutComplete').then(m => ({ default: m.DrawCheckoutCancel }))
);
const InvoiceCheckoutComplete = lazy(() =>
  import('./pages/payments/InvoiceCheckoutComplete').then(m => ({ default: m.default }))
);
const InvoiceCheckoutCancel = lazy(() =>
  import('./pages/payments/InvoiceCheckoutComplete').then(m => ({ default: m.InvoiceCheckoutCancel }))
);

// Initialize color theme from localStorage on page load
const savedColorTheme = localStorage.getItem('color-theme') || 'northline';
document.documentElement.setAttribute('data-theme', savedColorTheme);

function TenantProtectedRoute({ children }) {
  const token = localStorage.getItem('tenant_token');
  if (!token) return <Navigate to="/tenant/login" replace />;
  return children;
}

function ContractorProtectedRoute({ children }) {
  const token = localStorage.getItem('contractor_token');
  if (!token) return <Navigate to="/contractor/login" replace />;
  return children;
}

function PmProtectedRoute({ children }) {
  let token = null;
  try { token = localStorage.getItem('pm_token'); } catch { token = null; }
  if (!token) return <Navigate to="/pm/login" replace />;
  return children;
}

function RouteFallback() {
  return (
    <div
      className="flex items-center justify-center min-h-[40vh] text-sm text-slate-500 dark:text-slate-400"
      data-testid="route-suspense-fallback"
    >
      <div className="flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
        <span>Loading…</span>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <MetricRegistryProvider>
        <TenantAuthProvider>
          <ContractorAuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/security/two-factor" element={<TwoFactorSetupPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/beta-agreement" element={<BetaAgreementPage />} />
              <Route path="/security" element={<SecurityPage />} />

              {/* Tenant Portal Routes */}
              <Route path="/portal/accept/:token" element={<PortalAcceptInvitePage />} />
              <Route path="/tenant/login" element={<TenantLoginPage />} />
              <Route path="/tenant" element={<TenantProtectedRoute><TenantDashboard /></TenantProtectedRoute>} />
              <Route path="/tenant/payments" element={<TenantProtectedRoute><TenantPayments /></TenantProtectedRoute>} />
              <Route path="/tenant/maintenance" element={<TenantProtectedRoute><TenantMaintenance /></TenantProtectedRoute>} />
              <Route path="/tenant/messages" element={<TenantProtectedRoute><TenantMessages /></TenantProtectedRoute>} />
              <Route path="/tenant/messages/:woId" element={<TenantProtectedRoute><TenantMessages /></TenantProtectedRoute>} />

              {/* Contractor Portal Routes */}
              <Route path="/pm/login" element={<PmLoginPage />} />
              <Route path="/pm" element={<PmProtectedRoute><PmLayout /></PmProtectedRoute>}>
                <Route index element={<PmHomePage />} />
                <Route path="properties" element={<PmPropertiesPage />} />
                <Route path="properties/:propertyId" element={<PmPropertiesPage />} />
                <Route path="work-orders" element={<PmWorkOrdersPage />} />
                <Route path="work-orders/:woId" element={<PmWorkOrdersPage />} />
                <Route path="tenants" element={<PmTenantsPage />} />
                <Route path="tenants/:tenantId" element={<PmTenantsPage />} />
                <Route path="inspections" element={<PmInspectionsPage />} />
                <Route path="inspections/:inspectionId" element={<PmInspectionsPage />} />
                <Route path="documents" element={<PmDocumentsPage />} />
                <Route path="tasks" element={<PmTasksPage />} />
                <Route path="invoices" element={<PmInvoicesPage />} />
                <Route path="reports" element={<PmReportsPage />} />
                <Route path="notifications" element={<PmNotificationsPage />} />
              </Route>
              <Route path="/contractor/login" element={<ContractorLoginPage />} />
              <Route path="/contractor" element={<ContractorProtectedRoute><ContractorDashboard /></ContractorProtectedRoute>} />
              <Route path="/contractor/projects/:projectId" element={<ContractorProtectedRoute><ContractorProjectDetail /></ContractorProtectedRoute>} />
              <Route path="/contractor/work-orders/:woId" element={<ContractorProtectedRoute><ContractorWorkOrderWizard /></ContractorProtectedRoute>} />
              <Route path="/contractor/inbox" element={<ContractorProtectedRoute><ContractorInbox /></ContractorProtectedRoute>} />
              <Route path="/contractor/workspace" element={<ContractorProtectedRoute><ContractorWorkspace /></ContractorProtectedRoute>} />
              <Route path="/contractor/profile" element={<ContractorProtectedRoute><ContractorProfile /></ContractorProtectedRoute>} />
              <Route path="/contractor/gallery" element={<ContractorProtectedRoute><ContractorGalleryPage /></ContractorProtectedRoute>} />

              {/* Marketplace / Deal Network Routes
                  Sprint 1B — hidden from production sidebar, still accessible via direct URL. */}
              <Route path="/marketplace" element={<MarketplaceAuthProvider><MarketplaceListingsPage /></MarketplaceAuthProvider>} />
              <Route path="/marketplace/deals/:id" element={<MarketplaceAuthProvider><MarketplaceListingDetailPage /></MarketplaceAuthProvider>} />
              <Route path="/marketplace/login" element={<MarketplaceAuthProvider><MarketplaceLoginPage /></MarketplaceAuthProvider>} />
              <Route path="/marketplace/dashboard" element={<MarketplaceAuthProvider><MarketplaceDashboardPage /></MarketplaceAuthProvider>} />
              <Route path="/marketplace/dashboard/new" element={<MarketplaceAuthProvider><MarketplaceListingFormPage /></MarketplaceAuthProvider>} />
              <Route path="/marketplace/dashboard/edit/:id" element={<MarketplaceAuthProvider><MarketplaceListingFormPage /></MarketplaceAuthProvider>} />

              {/* Pricing (accessible with or without auth) */}
              <Route path="/pricing" element={<PricingPage />} />

              {/* STATUS-1 — the public status page (no sign-in) */}
              <Route path="/status" element={<StatusPage />} />

              {/* Public Tenant Application & Lease Signing (token-based, no auth) */}
              <Route path="/apply/:token" element={<PublicApplication />} />
              <Route path="/lease/:token" element={<LeaseRedirect />} />
              <Route path="/documents/sign/:token" element={<PublicDocumentSign />} />

              {/* Stripe Checkout return routes for contractor card payments
                  (hosted checkout fallback when embedded iframe is blocked) */}
              <Route path="/payments/contractor-checkout/complete" element={<ContractorCheckoutComplete />} />
              <Route path="/payments/contractor-checkout/cancel" element={<ContractorCheckoutCancel />} />
              <Route path="/payments/draw-checkout/complete" element={<DrawCheckoutComplete />} />
              <Route path="/payments/draw-checkout/cancel" element={<DrawCheckoutCancel />} />
              <Route path="/payments/invoice-checkout/complete" element={<InvoiceCheckoutComplete />} />
              <Route path="/payments/invoice-checkout/cancel" element={<InvoiceCheckoutCancel />} />

              {/* Admin Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="deals" element={<DealsListPage />} />
                <Route path="deals/offers" element={<OffersListPage />} />
                <Route path="deals/underwriting/:modelId" element={<UnderwritingModelRedirect />} />
                <Route path="deals/:id" element={<DealDetailPage />} />
                <Route path="properties" element={<PropertiesListPage />} />
                <Route path="properties/bulk-import" element={<BulkImportPage />} />
                <Route path="properties/:id" element={<RetiredPropertyTab><PropertyDetailPage /></RetiredPropertyTab>} />
                <Route path="users" element={<UsersPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="help" element={<HelpPage />} />
                <Route path="admin/company-access" element={<CompanyAccessPage />} />
                <Route path="admin/feedback" element={<FeedbackInboxPage />} />
                <Route path="admin/customer-health" element={<CustomerHealthPage />} />
                <Route path="admin/data-integrity" element={<DataIntegrityPage />} />
                <Route path="intelligence/operations" element={<AIOperationsPage />} />
                <Route path="intelligence/ask" element={<AskNorthlinePage />} />
                <Route path="contractors" element={<ContractorSection />} />
                <Route path="contractors/schedule" element={<ContractorSection />} />
                <Route path="contractors/approvals" element={<ContractorSection />} />
                <Route path="vendors" element={<VendorsPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                {/* Where an OAuth bank returns the browser. Must match
                    PLAID_REDIRECT_URI exactly, and that URI must be
                    registered in the Plaid dashboard — Plaid refuses to
                    mint a link token if the two disagree. */}
                <Route path="plaid/oauth-return" element={<PlaidOAuthReturnPage />} />
                <Route path="accounting/receivables" element={<Navigate to="/accounts?tab=receivables" replace />} />
                {/* TEN-1 — Lease Expirations folded into Tenants & Leasing.
                    Kept as a redirect so existing links and bookmarks land on
                    the tab rather than a blank route. */}
                <Route path="leases/expirations" element={<Navigate to="/tenants?tab=expirations" replace />} />
                <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
                <Route path="reconcile" element={<ReconcilePage />} />
                <Route path="tenants" element={<TenantsPage />} />
                <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
                <Route path="invoices" element={<InvoicesRedirect />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="workflow" element={<WorkflowPage />} />
                <Route path="construction" element={<ConstructionPage />} />
                <Route path="construction/projects/:projectId" element={<ProjectDetailPage />} />
                <Route path="construction/draws/:drawId" element={<DrawDetailPage />} />
                <Route path="property-managers" element={<PropertyManagersPage />} />
                <Route path="property-managers/:pmId" element={<PropertyManagersPage />} />
                <Route path="inspections" element={<InspectionsPage />} />
                <Route path="inspections/:inspectionId" element={<InspectionsPage />} />
                <Route path="work-orders" element={<WorkOrdersPage />} />
                <Route path="work-orders/requests/:requestId" element={<JobRequestPage />} />
                <Route path="work-orders/:woId" element={<WorkOrderDetailPage />} />
                <Route path="budgets" element={<BudgetsPage />} />
                <Route path="capital-events" element={<CapitalEventsPage />} />
                <Route path="reports" element={<ReportsPage />} />

                {/* Legacy redirect kept so any deep link to /alerts still lands somewhere useful. */}
                <Route path="alerts" element={<Navigate to="/workflow" replace />} />
                {/* APR-11c — retired approval/payment addresses land on the one
                    canonical surface (Michael, Part 13): nothing stays silently
                    dead in a bookmark, and nothing opens a second queue. */}
                <Route path="approvals" element={<Navigate to="/workflow?tab=approvals" replace />} />
                <Route path="my-approvals" element={<Navigate to="/workflow?tab=approvals" replace />} />
                <Route path="pay-queue" element={<Navigate to="/workflow?tab=approvals&kind=payments" replace />} />
                <Route path="payables/pay-queue" element={<Navigate to="/workflow?tab=approvals&kind=payments" replace />} />
                <Route path="payments/approvals" element={<Navigate to="/workflow?tab=approvals&kind=payments" replace />} />
                <Route path="taxes" element={<Navigate to="/accounts?tab=taxes" replace />} />
                <Route path="trash" element={<TrashPage />} />
              </Route>
            </Routes>
            </Suspense>
            <Toaster position="top-right" />
          </BrowserRouter>
          </ContractorAuthProvider>
        </TenantAuthProvider>
        </MetricRegistryProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
